import { lowerFirst } from 'lodash';
import { useCallback } from 'react';

import { createInterpolatedTranslationKey, resolveDeclensionTranslationKey, useTranslate } from '@dreryk/react-common';

import {
   AdministrationIntervalDays,
   DosagePeriod,
   DosageStageUnion,
   ExtendedDosage,
   isDosageBreak,
   isFrequencyDosageCycle,
   isTimesOfDayDosageCycle,
} from '~libs.shared.prescription';

import { translationKeys } from '../../translations';

import { getFormattedPortion, getTimesOfDaySchema } from './ExtendedDosageStagesList.utils';

type FormatPeriod = (period: DosagePeriod) => string;

type FormatStage = (stage: DosageStageUnion, portionUnit?: string | null) => string;

type FormatStages = (stages: ExtendedDosage['stages'], portionUnit?: ExtendedDosage['portionUnit'] | null) => string[];

const useFormatPeriod = (): FormatPeriod => {
   const translate = useTranslate();

   return useCallback(
      (period: DosagePeriod): string => {
         const { timeUnitMap, timeUnitShortMap } = translationKeys.extendedDosage;
         const unit =
            period.unit === 'DAY'
               ? resolveDeclensionTranslationKey(
                    period.amount,
                    timeUnitMap.DAY.accusative,
                    timeUnitMap.DAY.genitive,
                    timeUnitMap.DAY.plural
                 )
               : timeUnitShortMap[period.unit];

         return `${period.amount} ${translate(unit)}`;
      },
      [translate]
   );
};

export const useFormatStage = (): FormatStage => {
   const translate = useTranslate();
   const formatPeriod = useFormatPeriod();

   return useCallback(
      (stage: DosageStageUnion, portionUnit?: string | null): string => {
         const keys = translationKeys.extendedDosage;
         const withPortionUnit = (value: string): string => [value, portionUnit].filter(Boolean).join(' ');

         if (isFrequencyDosageCycle(stage)) {
            return translate(
               createInterpolatedTranslationKey(
                  portionUnit ? keys.frequencyCycleSummaryWithUnit : keys.frequencyCycleSummary,
                  {
                     portion: getFormattedPortion(stage.portion),
                     unit: portionUnit ?? '',
                     interval: formatPeriod(stage.interval),
                     duration: formatPeriod(stage.duration),
                  }
               )
            );
         }

         if (isTimesOfDayDosageCycle(stage)) {
            const administrationIntervalDays = Number(stage.administrationIntervalDays) as AdministrationIntervalDays;

            return translate(
               createInterpolatedTranslationKey(keys.timesOfDayCycleSummary, {
                  schema: withPortionUnit(getTimesOfDaySchema(stage)),
                  administration: translate(keys.administrationIntervalMap[administrationIntervalDays]),
                  duration: formatPeriod(stage.duration),
               })
            );
         }

         if (isDosageBreak(stage)) {
            return translate(
               createInterpolatedTranslationKey(keys.breakSummary, { duration: formatPeriod(stage.duration) })
            );
         }

         return '';
      },
      [translate, formatPeriod]
   );
};

export const useFormatStages = (): FormatStages => {
   const formatStage = useFormatStage();

   return useCallback(
      (stages: ExtendedDosage['stages'], portionUnit?: ExtendedDosage['portionUnit'] | null): string[] =>
         stages.map((stage) => formatStage(stage, portionUnit)).map(lowerFirst),
      [formatStage]
   );
};
